import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Cookie, X, Settings, CheckCircle2 } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem('agrarkapital_cookie_consent');
    if (!cookieConsent) {
      // Show banner after 2 seconds
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      // Load saved preferences
      const savedSettings = JSON.parse(cookieConsent);
      setCookieSettings(savedSettings);
      
      // Initialize tracking based on saved preferences
      if (savedSettings.analytics) {
        // Lade Google Analytics dynamisch
        loadGoogleAnalytics();
      }
      if (savedSettings.marketing) {
        // Lade Meta Pixel dynamisch
        loadMetaPixel();
      }
    }
  }, []);

  const loadGoogleAnalytics = () => {
    // Prüfe ob bereits geladen
    if (window.gtag) {
      console.log('[GA4] Already loaded');
      return;
    }
    
    console.log('[GA4] Loading Google Analytics...');
    
    // Erstelle gtag Script
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-M0V7H4DM3T';
    script.async = true;
    document.head.appendChild(script);
    
    // Initialisiere gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('consent', 'default', {
      'analytics_storage': 'granted',
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted'
    });
    gtag('config', 'G-M0V7H4DM3T', {
      'anonymize_ip': true
    });
    
    console.log('[GA4] Google Analytics loaded successfully');
  };

  const loadMetaPixel = () => {
    // Prüfe ob bereits geladen
    if (window.fbq) {
      console.log('[Meta] Already loaded');
      return;
    }
    
    console.log('[Meta] Loading Meta Pixel...');
    
    // Lade Meta Pixel
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('consent', 'grant');
    window.fbq('init', '643688211932612');
    window.fbq('track', 'PageView');
    
    console.log('[Meta] Meta Pixel loaded successfully');
  };

  const handleAcceptAll = () => {
    const newSettings = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setCookieSettings(newSettings);
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(newSettings));
    
    // Lade Tracking-Scripts
    loadGoogleAnalytics();
    loadMetaPixel();
    
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const newSettings = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setCookieSettings(newSettings);
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(newSettings));
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(cookieSettings));
    
    // Lade Tracking-Scripts basierend auf Einstellungen
    if (cookieSettings.analytics) {
      loadGoogleAnalytics();
    }
    if (cookieSettings.marketing) {
      loadMetaPixel();
    }
    
    setShowSettings(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div data-testid="cookie-banner" className="ak-cookie-banner fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="container mx-auto max-w-4xl">
        {!showSettings ? (
        // Main Cookie Banner - ZENTRIERT & AUFFÄLLIG
        <Card className="bg-white shadow-2xl border-4 border-[#C6A15B]">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#C6A15B] rounded-lg flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-white" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-[#0F3D2E] mb-2">
                  Diese Website verwendet Cookies
                </h3>
                <p className="text-gray-600 text-sm md:text-base mb-4 leading-relaxed">
                  Wir verwenden Cookies für Grundfunktionen, Website-Verbesserung und personalisierte Werbung. 
                  Ihre Einwilligung ist freiwillig.
                </p>
                
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <Button 
                    onClick={handleAcceptAll}
                    className="bg-[#0F3D2E] hover:bg-[#0F3D2E]/90 text-white px-8 py-3 font-semibold"
                  >
                    Alle akzeptieren
                  </Button>
                  
                  <Button 
                    onClick={handleAcceptNecessary}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 font-semibold"
                  >
                    Nur notwendige
                  </Button>
                  
                  <Button 
                    onClick={() => setShowSettings(true)}
                    className="bg-[#C6A15B] hover:bg-[#C6A15B]/90 text-white px-8 py-3 font-semibold flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Einstellungen</span>
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 mt-4">
                  Mehr Informationen: <a href="/datenschutz" className="text-[#C6A15B] hover:underline font-semibold">Datenschutzerklärung</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Cookie Settings Panel
        <Card className="bg-white shadow-2xl border-2 border-[#C6A15B]/20 max-w-2xl mx-auto">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-[#0F3D2E]">Cookie-Einstellungen</h3>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-[#0F3D2E] p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-[#0F3D2E]">Notwendige Cookies</h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-green-500 rounded-full flex items-center">
                    <div className="w-5 h-5 bg-white rounded-full ml-1"></div>
                  </div>
                </div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0F3D2E] mb-2">Analytics-Cookies</h4>
                  <p className="text-gray-600 text-sm">
                    Helfen uns zu verstehen, wie Besucher unsere Website nutzen (Google Analytics).
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setCookieSettings(prev => ({ ...prev, analytics: !prev.analytics }))}
                    className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                      cookieSettings.analytics ? 'bg-[#C6A15B]' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      cookieSettings.analytics ? 'translate-x-6' : 'translate-x-1'
                    }`}></div>
                  </button>
                </div>
              </div>
              
              {/* Marketing Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0F3D2E] mb-2">Marketing-Cookies</h4>
                  <p className="text-gray-600 text-sm">
                    Ermöglichen personalisierte Werbung und Remarketing (Facebook Pixel).
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setCookieSettings(prev => ({ ...prev, marketing: !prev.marketing }))}
                    className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                      cookieSettings.marketing ? 'bg-[#C6A15B]' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      cookieSettings.marketing ? 'translate-x-6' : 'translate-x-1'
                    }`}></div>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
              <Button 
                onClick={handleSaveSettings}
                className="bg-[#C6A15B] hover:bg-[#C6A15B]/90 text-white px-6 py-2 flex-1"
              >
                Einstellungen speichern
              </Button>
              
              <Button 
                onClick={handleAcceptAll}
                variant="outline"
                className="border-[#0F3D2E] text-[#0F3D2E] hover:bg-[#0F3D2E] hover:text-white px-6 py-2 flex-1"
              >
                Alle akzeptieren
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default CookieBanner;